import React, { FC, useEffect, useRef, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import colors from '@packages/core/styles/colors';
import { Icon } from '@packages/ui/shared';
import { QuickReference } from '../../types';
import { QuickReferenceIconProps } from './quick-reference-tabs';

export const QUICK_REFERENCE_CARD_WIDTH = 268;

const NOTICE_DURATION_MS = 2600;

const useStyles = createUseStyles({
    card: {
        position: 'relative',
        flex: '0 0 auto',
        width: QUICK_REFERENCE_CARD_WIDTH,
        scrollSnapAlign: 'start',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: colors.white,
        border: `1px solid ${colors.grayThree}`,
        borderRadius: 8,
        overflow: 'hidden',
        transition: 'border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease',
        '&:hover': {
            borderColor: colors.blueTwo,
            boxShadow: '0 8px 20px rgba(7, 20, 36, 0.1)',
            transform: 'translateY(-2px)',
        },
        '@media (prefers-reduced-motion: reduce)': {
            transition: 'none',
            '&:hover': { transform: 'none' },
        },
    },
    previewArea: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        padding: 0,
        border: 'none',
        background: 'none',
        textAlign: 'left',
        cursor: 'pointer',
        '&:focus-visible': {
            outline: `2px solid ${colors.blueOne}`,
            outlineOffset: -2,
        },
    },
    previewAreaInert: {
        cursor: 'default',
    },
    media: {
        position: 'relative',
        height: 96,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `linear-gradient(135deg, ${colors.blueFour} 0%, ${colors.grayOne} 100%)`,
        borderBottom: `1px solid ${colors.grayThree}`,
    },
    body: {
        flex: 1,
        padding: '12px 14px 0',
    },
    title: {
        margin: 0,
        fontSize: 14,
        fontWeight: 600,
        lineHeight: '19px',
        color: colors.textDarkPrimary,
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
    },
    footer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
        padding: '10px 14px 12px',
    },
    version: {
        fontSize: 12,
        lineHeight: '16px',
        color: colors.textDarkSecondary,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    download: {
        flex: '0 0 auto',
        width: 32,
        height: 32,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
        border: 'none',
        borderRadius: '50%',
        backgroundColor: colors.grayTwo,
        cursor: 'pointer',
        transition: 'background-color 160ms ease',
        '&:hover': {
            backgroundColor: colors.blueFour,
        },
        '&:focus-visible': {
            outline: `2px solid ${colors.blueOne}`,
            outlineOffset: 2,
        },
    },
    spinner: {
        width: 16,
        height: 16,
        borderRadius: '50%',
        border: `2px solid ${colors.blueThree}`,
        borderTopColor: colors.blueOne,
        animation: '$spin 700ms linear infinite',
    },
    mediaSpinner: {
        width: 28,
        height: 28,
        borderWidth: 3,
    },
    notice: {
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0 16px',
        textAlign: 'center',
        fontSize: 12,
        fontWeight: 600,
        lineHeight: '16px',
        color: colors.textDarkSecondary,
        backgroundColor: colors.grayOne,
        animation: '$fadeIn 160ms ease',
    },
    unavailable: {
        opacity: 0.6,
        '&:hover': {
            borderColor: colors.grayThree,
            boxShadow: 'none',
            transform: 'none',
        },
        // The controls stay focusable and keep their tooltip, so the cursor is what has to
        // say they are inert.
        '& button': {
            cursor: 'not-allowed',
        },
    },
    tooltip: {
        fontSize: 13,
        maxWidth: 320,
        textAlign: 'left',
    },
    '@keyframes spin': {
        to: { transform: 'rotate(360deg)' },
    },
    '@keyframes fadeIn': {
        from: { opacity: 0 },
        to: { opacity: 1 },
    },
});

export interface QuickReferenceCardProps {
    reference: QuickReference;
    TypeIcon: FC<QuickReferenceIconProps>;
    canPreview: boolean;
    onPreview: () => Promise<void>;
    onDownload: () => Promise<void>;
}

export const QuickReferenceCard: FC<QuickReferenceCardProps> = ({
    reference,
    TypeIcon,
    canPreview,
    onPreview,
    onDownload,
}) => {
    const classes = useStyles();
    const [isPreviewing, setIsPreviewing] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [showNotice, setShowNotice] = useState(false);
    const noticeTimer = useRef<ReturnType<typeof setTimeout>>();

    useEffect(() => {
        return () => clearTimeout(noticeTimer.current);
    }, []);

    const isUnavailable = !reference.downloadUrl;

    const handlePreview = async () => {
        if (isUnavailable || isPreviewing) {
            return;
        }
        if (!canPreview) {
            clearTimeout(noticeTimer.current);
            setShowNotice(true);
            noticeTimer.current = setTimeout(() => setShowNotice(false), NOTICE_DURATION_MS);
            return;
        }
        setIsPreviewing(true);
        try {
            await onPreview();
        } finally {
            setIsPreviewing(false);
        }
    };

    const handleDownload = async () => {
        if (isUnavailable || isDownloading) {
            return;
        }
        setIsDownloading(true);
        try {
            await onDownload();
        } finally {
            setIsDownloading(false);
        }
    };

    const previewLabel = isUnavailable
        ? `${reference.documentTitle} (not available)`
        : canPreview
          ? `Preview ${reference.documentTitle}`
          : `${reference.documentTitle} (no preview available, download to view)`;

    return (
        <div
            className={`${classes.card} ${isUnavailable ? classes.unavailable : ''}`}
            data-testid="quick-reference-card"
        >
            <OverlayTrigger
                placement="bottom"
                delay={{ show: 400, hide: 100 }}
                overlay={
                    <Tooltip
                        id={`quick-reference-${encodeURIComponent(
                            `${reference.documentTitle}::${reference.version ?? 'no-version'}`
                        )}`}
                        className={classes.tooltip}
                    >
                        {reference.documentTitle}
                    </Tooltip>
                }
            >
                <button
                    type="button"
                    data-testid="quick-reference-preview"
                    className={`${classes.previewArea} ${canPreview ? '' : classes.previewAreaInert}`}
                    aria-disabled={isUnavailable}
                    aria-busy={isPreviewing}
                    aria-label={previewLabel}
                    onClick={handlePreview}
                >
                    <div className={classes.media}>
                        {isPreviewing ? (
                            <div
                                className={`${classes.spinner} ${classes.mediaSpinner}`}
                                data-testid="quick-reference-previewing"
                            />
                        ) : (
                            <TypeIcon size={36} color={colors.blueOne} />
                        )}
                        {showNotice && (
                            <div className={classes.notice} role="status" data-testid="quick-reference-no-preview">
                                Nothing to preview - please download.
                            </div>
                        )}
                    </div>

                    <div className={classes.body}>
                        <p className={classes.title}>{reference.documentTitle}</p>
                    </div>
                </button>
            </OverlayTrigger>

            <div className={classes.footer}>
                <span className={classes.version}>
                    {reference.version ? `Version: ${reference.version}` : 'No version'}
                </span>
                <button
                    type="button"
                    className={classes.download}
                    aria-disabled={isUnavailable}
                    aria-busy={isDownloading}
                    aria-label={`Download ${reference.documentTitle}`}
                    onClick={handleDownload}
                >
                    {isDownloading ? (
                        <div className={classes.spinner} data-testid="quick-reference-downloading" />
                    ) : (
                        <Icon name="download" size={18} color="blueOne" />
                    )}
                </button>
            </div>
        </div>
    );
};
